import React, { useEffect, useState } from 'react';
import Editor from '@monaco-editor/react';
import { ChangeSet, ChangesPreview, SaveResult } from './ChangeTracker';
import './SaveChangesDialog.scss';

interface SaveChangesDialogProps<T = unknown> {
  isOpen: boolean;
  changes: ChangeSet<T>;
  onSave: () => Promise<SaveResult>;
  /** Given, the dialog shows the SQL the save would run, fetched when it opens. */
  onPreview?: () => Promise<ChangesPreview>;
  onCancel: () => void;
  getRowLabel?: (row: T) => string;
}

export function SaveChangesDialog<T = unknown>({
  isOpen,
  changes,
  onSave,
  onPreview,
  onCancel,
  getRowLabel = (row: unknown) => JSON.stringify(row),
}: SaveChangesDialogProps<T>) {
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<SaveResult | null>(null);
  const [preview, setPreview] = useState<ChangesPreview | 'loading' | null>(null);
  // the review box grows with its content (wrapped lines included), up to a cap
  const [previewHeight, setPreviewHeight] = useState(60);

  useEffect(() => {
    if (!isOpen || !onPreview) return;
    let cancelled = false;
    setPreview('loading');
    onPreview()
      .then((p) => !cancelled && setPreview(p))
      .catch((e) => !cancelled && setPreview({ statements: [], error: String(e) }));
    return () => {
      cancelled = true;
    };
    // fetched once per opening; the changes cannot change while the dialog is open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const { inserts, updates, deletes } = changes;
  const totalChanges = inserts.length + updates.size + deletes.length;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await onSave();
      setSaveResult(result);
    } catch (error) {
      setSaveResult({
        success: false,
        summary: { inserted: 0, updated: 0, deleted: 0, failed: totalChanges },
        errors: new Map([['error', String(error)]]),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSaveResult(null);
    onCancel();
  };

  return (
    <div className="save-changes-dialog-overlay" onClick={handleClose}>
      <div className="save-changes-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Save Changes</h2>
          <button className="close-button" onClick={handleClose} title="Close">
            ✕
          </button>
        </div>

        <div className="dialog-content">
          {!saveResult ? (
            <>
              <div className="changes-summary">
                <p className="summary-text">You have <strong>{totalChanges}</strong> unsaved change{totalChanges !== 1 ? 's' : ''}:</p>
                <ul className="summary-list">
                  {inserts.length > 0 && (
                    <li className="insert-item">
                      <span className="change-icon">➕</span>
                      <strong>{inserts.length}</strong> row{inserts.length !== 1 ? 's' : ''} inserted
                    </li>
                  )}
                  {updates.size > 0 && (
                    <li className="update-item">
                      <span className="change-icon">✏️</span>
                      <strong>{updates.size}</strong> row{updates.size !== 1 ? 's' : ''} updated
                    </li>
                  )}
                  {deletes.length > 0 && (
                    <li className="delete-item">
                      <span className="change-icon">🗑️</span>
                      <strong>{deletes.length}</strong> row{deletes.length !== 1 ? 's' : ''} deleted
                    </li>
                  )}
                </ul>
              </div>

              {onPreview && (
                <div className="sql-preview">
                  <div className="sql-preview-title">SQL the save will run, in one transaction</div>
                  {preview === 'loading' || preview === null ? (
                    <div className="sql-preview-note">Building the statements…</div>
                  ) : preview.error ? (
                    <div className="sql-preview-error">{preview.error}</div>
                  ) : (
                    <>
                      <div className="sql-preview-editor">
                        <Editor
                          height={`${previewHeight}px`}
                          onMount={(ed) => {
                            const fit = () => setPreviewHeight(Math.min(320, Math.max(40, ed.getContentHeight() + 8)));
                            ed.onDidContentSizeChange(fit);
                            fit();
                          }}
                          language="sql"
                          theme="vs-dark"
                          value={preview.statements.join('\n')}
                          options={{
                            readOnly: true,
                            domReadOnly: true,
                            minimap: { enabled: false },
                            scrollBeyondLastLine: false,
                            wordWrap: 'on',
                            fontSize: 12,
                            lineNumbers: 'off',
                            scrollbar: { alwaysConsumeMouseWheel: false },
                            automaticLayout: true,
                          }}
                        />
                      </div>
                      {preview.note && <div className="sql-preview-note">{preview.note}</div>}
                    </>
                  )}
                </div>
              )}

              <div className="changes-detail">
                {inserts.length > 0 && (
                  <details className="change-section">
                    <summary>Inserted Rows ({inserts.length})</summary>
                    <ul>
                      {inserts.map((insert, i) => (
                        <li key={i}>
                          <code>{getRowLabel(insert.row)}</code>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                {updates.size > 0 && (
                  <details className="change-section">
                    <summary>Updated Rows ({updates.size})</summary>
                    <ul>
                      {Array.from(updates.entries()).map(([rowId, cellChanges]) => (
                        <li key={rowId}>
                          <div className="row-label">Row ID: {rowId}</div>
                          <ul className="cell-changes">
                            {cellChanges.map((change, i) => (
                              <li key={i}>
                                <strong>{change.columnId}:</strong>{' '}
                                <span className="old-value">{String(change.oldValue)}</span> →{' '}
                                <span className="new-value">{String(change.newValue)}</span>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                {deletes.length > 0 && (
                  <details className="change-section">
                    <summary>Deleted Rows ({deletes.length})</summary>
                    <ul>
                      {deletes.map((del, i) => (
                        <li key={i}>
                          <code>{getRowLabel(del.row)}</code>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </>
          ) : (
            <div className={`save-result ${saveResult.success ? 'success' : 'error'}`}>
              {saveResult.success ? (
                <>
                  <div className="success-icon">✅</div>
                  <h3>Changes Saved Successfully</h3>
                  <div className="result-summary">
                    {saveResult.summary.inserted > 0 && <p>✅ {saveResult.summary.inserted} row(s) inserted</p>}
                    {saveResult.summary.updated > 0 && <p>✅ {saveResult.summary.updated} row(s) updated</p>}
                    {saveResult.summary.deleted > 0 && <p>✅ {saveResult.summary.deleted} row(s) deleted</p>}
                  </div>
                </>
              ) : (
                <>
                  <div className="error-icon">❌</div>
                  <h3>Save Failed</h3>
                  <div className="result-summary">
                    {saveResult.summary.failed > 0 && <p>❌ {saveResult.summary.failed} change(s) failed</p>}
                    {saveResult.errors && (
                      <div className="error-details">
                        <h4>Errors:</h4>
                        <ul>
                          {Array.from(saveResult.errors.entries()).map(([key, error]) => (
                            <li key={key}>
                              <strong>{key}:</strong> {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="dialog-footer">
          {!saveResult ? (
            <>
              <button className="cancel-button" onClick={handleClose} disabled={isSaving}>
                Cancel
              </button>
              <button className="save-button" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : `Save ${totalChanges} Change${totalChanges !== 1 ? 's' : ''}`}
              </button>
            </>
          ) : (
            <button className="close-button-footer" onClick={handleClose}>
              {saveResult.success ? 'Close' : 'Back'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
